import { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Tooltip from 'components/Tooltip/Tooltip';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { createPopper } from '@popperjs/core';
import { useModalContext } from 'context/ModalContext';
import { useMutation, useQuery } from 'react-query';
import { getProfileUser, requestUpgrade } from 'api/users/api';
import Loader from 'components/Loader/Loader';
import clsx from 'clsx';
import { UserProfileResponse } from 'api/users/schemas/UserProfile';
import { G360Token } from 'api/auth/schema';

const LOCAL_STORAGE_KEY = 'hasRequestedUpgrade';

export const useUpgradeAccessTooltip = () => {
  const [hasRequestedUpgrade, setHasRequestedUpgrade] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null,
  );
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);

  const { openModal } = useModalContext();

  const g360Token: G360Token | undefined = JSON.parse(
    localStorage.getItem('g360-token-storage') ?? '{}',
  );

  const storageKey = useMemo(() => {
    return `${LOCAL_STORAGE_KEY}-${g360Token?.user_id}-${g360Token?.group_id}`;
  }, [g360Token?.group_id, g360Token?.user_id]);

  useEffect(() => {
    const storedValue = localStorage.getItem(storageKey) === 'true';
    setHasRequestedUpgrade(storedValue);
  }, [storageKey]);

  const { data } = useQuery('user-profile-actions', getProfileUser, {
    enabled: localStorage.getItem(storageKey) === null,
    onSuccess: (data: UserProfileResponse) => {
      if (data?.actions?.role_upgrade_requested) {
        localStorage.setItem(storageKey, 'true');
        setHasRequestedUpgrade(true);
      }
    },
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  const upgradeRequestModal = useMutation(requestUpgrade, {
    onSuccess: () => {
      openModal();
      localStorage.setItem(storageKey, 'true');
      setHasRequestedUpgrade(true);
    },
  });

  useEffect(() => {
    if (isTooltipVisible && referenceElement && tooltipRef.current) {
      const popperInstance = createPopper(
        referenceElement,
        tooltipRef.current,
        {
          placement: 'top',
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ],
        },
      );
      return () => {
        popperInstance.destroy();
      };
    }
  }, [isTooltipVisible, referenceElement]);

  const showTooltip = (event: React.MouseEvent<HTMLElement>) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setReferenceElement(event.currentTarget as HTMLElement);
    setIsTooltipVisible(true);
  };

  const hideTooltip = () => {
    timerRef.current = window.setTimeout(() => {
      setIsTooltipVisible(false);
    }, 200);
  };

  const handleRequestUpgrade = () => {
    if (upgradeRequestModal.isLoading) return;
    upgradeRequestModal.mutate();
  };

  const keepTooltipVisible = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setIsTooltipVisible(true);
  };

  const renderTooltip = () => {
    if (!isTooltipVisible) return null;

    return ReactDOM.createPortal(
      <div
        ref={tooltipRef}
        className="relative"
        onMouseEnter={keepTooltipVisible}
        onMouseLeave={hideTooltip}
      >
        <Tooltip
          content={
            <div className="overflow-wrap flex flex-col items-start gap-2 break-words">
              <p className="font-bold text-content-base-default">
                Insufficient permissions
              </p>
              <p className="text-sm text-content-base-subdued">
                You do not have the required role to perform this action. Please
                request a role upgrade from your Company Admin.
              </p>
              {hasRequestedUpgrade ||
              data?.actions?.role_upgrade_requested === true ? (
                <GraniteButton variant="tertiary" disabled className="mt-3">
                  Role upgrade requested
                </GraniteButton>
              ) : (
                <GraniteButton
                  variant="tertiary"
                  onClick={handleRequestUpgrade}
                  className={clsx(
                    'mt-3',
                    upgradeRequestModal.isLoading &&
                      '!border-button-stroke-tertiary-pressed !cursor-default !bg-button-background-tertiary-pressed !text-button-content-tertiary-pressed',
                  )}
                >
                  Request role upgrade
                  {upgradeRequestModal.isLoading && (
                    <Loader animationClassname="!w-3 !h-3" />
                  )}
                </GraniteButton>
              )}
            </div>
          }
        />
      </div>,
      document.body,
    );
  };

  return {
    showTooltip,
    hideTooltip,
    renderTooltip,
    isTooltipVisible,
    keepTooltipVisible,
    referenceElement,
  };
};
